import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';

export function UserRegister() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleRegister = (e) => {
    e.preventDefault();
    login(); // Set authenticated state
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left: Register Form */}
      <div className="flex-1 flex items-center justify-center px-8 py-12 lg:px-20">
        <div className="w-full max-w-[400px]">
          {/* Logo + Live text - standardized header */}
          <Link to="/" className="flex items-center gap-2 mb-12">
            <img 
              src="/exit88-logo.png" 
              alt="EXIT88" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-primary font-bold text-xl">LIVE</span>
          </Link>

          <div className="space-y-10">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
              <p className="text-muted-foreground text-base">Join EXIT88 LIVE and never miss an event</p>
            </div>

            <form onSubmit={handleRegister} className="space-y-5">
              <div className="space-y-2">
                <Input 
                  type="text"
                  placeholder="Enter Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-transparent border-border/60 h-12 text-white placeholder:text-muted-foreground/70"
                />
              </div>

              <div className="space-y-2">
                <Input 
                  type="email"
                  placeholder="Enter Email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-transparent border-border/60 h-12 text-white placeholder:text-muted-foreground/70"
                />
              </div>

              <div className="space-y-2">
                <div className="relative">
                  <Input 
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create Password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="bg-transparent border-border/60 h-12 text-white placeholder:text-muted-foreground/70 pr-12"
                  />
                  <button 
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Input 
                  type="password"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="bg-transparent border-border/60 h-12 text-white placeholder:text-muted-foreground/70"
                />
              </div>

              <div className="flex items-start gap-2">
                <input type="checkbox" className="rounded border-border mt-1" />
                <span className="text-sm text-muted-foreground">
                  I agree to the <Link to="#" className="text-primary hover:underline">Terms of Service</Link> and{' '}
                  <Link to="#" className="text-primary hover:underline">Privacy Policy</Link>
                </span>
              </div>

              <div className="pt-2">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border/40"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-background text-muted-foreground/80">Or continue with</span>
                  </div>
                </div>

                {/* Social buttons - matching Login page style */}
                <div className="flex justify-center gap-4 mt-6">
                  <button 
                    type="button"
                    className="w-12 h-12 rounded-lg border border-primary/50 flex items-center justify-center hover:bg-primary/10 transition-colors"
                    data-testid="google-signup-btn"
                  >
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </button>
                  <button 
                    type="button"
                    className="w-12 h-12 rounded-lg border border-primary/50 flex items-center justify-center hover:bg-primary/10 transition-colors"
                    data-testid="apple-signup-btn"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                    </svg>
                  </button>
                  <button 
                    type="button"
                    className="w-12 h-12 rounded-lg border border-primary/50 flex items-center justify-center hover:bg-primary/10 transition-colors"
                    data-testid="facebook-signup-btn"
                  >
                    <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </button>
                </div>
              </div>

              <p className="text-center text-muted-foreground pt-4">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline font-semibold">Log In</Link>
              </p>

              <Button 
                type="submit" 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base font-semibold rounded-full"
              >
                SIGN UP
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Right: Hero Image */}
      <div className="hidden lg:flex flex-1 relative">
        <img 
          src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1200&q=80" 
          alt="Festival" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/50 to-transparent" />
        <div className="relative z-10 flex flex-col justify-center p-12 max-w-lg">
          <h2 className="text-4xl font-bold text-white mb-4">Join the EXIT88 Community</h2>
          <p className="text-lg text-gray-300">Get early access to tickets, exclusive content, and live streams from your favorite artists.</p>
        </div>
      </div>
    </div>
  );
}

export default UserRegister;
